import React, { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { typesofUsers } from '../../config';

declare global {
  interface Window {
    __googleTranslateInit?: () => void;
  }
}

let googleTranslateScriptPromise: Promise<void> | null = null;
let googleTranslateElementInitialized = false;
let googleTranslateHost: HTMLDivElement | null = null;

// Curated list to keep the dropdown compact and on-screen.
// Add/remove codes as business needs change.
const ALLOWED_LANGUAGES = ['en', 'es', 'fr', 'de', 'it', 'pt'];
const APPLICANT_VERIFICATION_ROUTE = '/admin/docupload_applicant/';

const getTranslateMenuFrame = () => document.querySelector('.goog-te-menu-frame.skiptranslate') as HTMLIFrameElement | null;

let translateMenuFixedPos: { left: number; top: number } | null = null;

const positionTranslateMenu = () => {
  const frame = getTranslateMenuFrame();
  const host = googleTranslateHost;
  if (!frame || !host) return;
  if (frame.style.display === 'none') return;

  if (!translateMenuFixedPos) {
    const rect = host.getBoundingClientRect();
    const margin = 4;
    const left = Math.min(Math.max(rect.left, 8), window.innerWidth - (frame.offsetWidth || 280) - 8);
    const top = rect.bottom + margin;
    translateMenuFixedPos = { left, top };
  }

  frame.style.position = 'fixed';
  frame.style.left = `${translateMenuFixedPos.left}px`;
  frame.style.top = `${translateMenuFixedPos.top}px`;
  frame.style.transform = 'none';
  frame.style.opacity = '1';
  frame.style.visibility = 'visible';
  frame.style.pointerEvents = 'auto';
  frame.style.zIndex = '2147483647';
};

export const resetGoogleTranslateStateForTests = () => {
  googleTranslateScriptPromise = null;
  googleTranslateElementInitialized = false;
  googleTranslateHost = null;

  const script = document.getElementById('google-translate-script');
  if (script) {
    script.remove();
  }

  delete window.__googleTranslateInit;
};

const loadGoogleTranslateScript = () => {
  if ((window as any).google?.translate?.TranslateElement) {
    return Promise.resolve();
  }

  if (googleTranslateScriptPromise) {
    return googleTranslateScriptPromise;
  }

  googleTranslateScriptPromise = new Promise<void>((resolve, reject) => {
    const scriptId = 'google-translate-script';
    const existingScript = document.getElementById(scriptId);
    let settled = false;

    const resolveOnce = () => {
      if (settled) {
        return;
      }

      settled = true;
      resolve();
      delete window.__googleTranslateInit;
    };

    const rejectOnce = () => {
      if (settled) {
        return;
      }

      settled = true;
      googleTranslateScriptPromise = null;
      delete window.__googleTranslateInit;
      reject(new Error('Failed to load Google Translate.'));
    };

    window.__googleTranslateInit = () => {
      resolveOnce();
    };

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://translate.google.com/translate_a/element.js?cb=__googleTranslateInit';
    script.async = true;
    script.onload = () => {
      if ((window as any).google?.translate?.TranslateElement) {
        resolveOnce();
      }
    };
    script.onerror = () => {
      script.remove();
      rejectOnce();
    };

    if (existingScript) {
      existingScript.remove();
    }

    document.body.appendChild(script);
  });

  return googleTranslateScriptPromise;
};

const GOOGLE_TRANSLATE_HOST_ID = 'google_translate_element_singleton';

const getOrCreateGoogleTranslateHost = () => {
  if (!googleTranslateHost) {
    googleTranslateHost = document.createElement('div');
    googleTranslateHost.id = GOOGLE_TRANSLATE_HOST_ID;
    googleTranslateHost.className = 'google-translate-element';
  }

  return googleTranslateHost;
};

const syncGoogleTranslateHostToSlot = () => {
  const host = getOrCreateGoogleTranslateHost();
  const slot = document.querySelector('[data-google-translate-slot="true"]') as HTMLElement | null;

  if (slot) {
    if (host.parentElement !== slot) {
      slot.innerHTML = '';
      slot.appendChild(host);
    }
    host.style.display = '';
    return;
  }

  host.style.display = 'none';
};

const hasGoogleTranslateSlot = () => {
  return Boolean(document.querySelector('[data-google-translate-slot="true"]'));
};

const getStoredUserRole = () => {
  const auth = localStorage.getItem('auth');

  if (!auth) {
    return null;
  }

  try {
    return JSON.parse(auth)?.data?.userrole ?? null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const shouldEnableGoogleTranslate = (pathname: string, role?: string | null) => {
  return isApplicantRole(role) || pathname.includes(APPLICANT_VERIFICATION_ROUTE);
};

const syncTranslateProtection = (shouldEnableTranslate: boolean) => {
  [document.documentElement, document.body, document.getElementById('root')].forEach((node) => {
    if (!node) {
      return;
    }

    node.classList.toggle('notranslate', !shouldEnableTranslate);
    node.setAttribute('translate', shouldEnableTranslate ? 'yes' : 'no');
  });
};

export const GoogleTranslateManager: React.FC = () => {
  const location = useLocation();
  const shouldEnableTranslate = shouldEnableGoogleTranslate(location.pathname, getStoredUserRole());

  useLayoutEffect(() => {
    syncTranslateProtection(shouldEnableTranslate);

    if (!shouldEnableTranslate) {
      getOrCreateGoogleTranslateHost().style.display = 'none';
    }
  }, [shouldEnableTranslate]);

  useEffect(() => {
    if (!shouldEnableTranslate) {
      return;
    }

    let isMounted = true;
    let isInitializing = false;
    const host = getOrCreateGoogleTranslateHost();
    translateMenuFixedPos = null;

    const initializeTranslate = async () => {
      if (isInitializing) {
        return;
      }

      isInitializing = true;
      await loadGoogleTranslateScript();

      const google = (window as any).google;
      if (!isMounted || !google?.translate?.TranslateElement || !hasGoogleTranslateSlot()) {
        isInitializing = false;
        return;
      }

      syncGoogleTranslateHostToSlot();
      positionTranslateMenu();

      if (!googleTranslateElementInitialized) {
        host.innerHTML = '';
        new google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: ALLOWED_LANGUAGES.join(','),
            layout: google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          GOOGLE_TRANSLATE_HOST_ID
        );
        googleTranslateElementInitialized = true;
      }

      syncGoogleTranslateHostToSlot();
      positionTranslateMenu();
      isInitializing = false;
    };

    initializeTranslate().catch((error) => {
      isInitializing = false;
      console.error(error);
    });

    translateMenuFixedPos = null;

    const observer = new MutationObserver(() => {
      syncGoogleTranslateHostToSlot();
      positionTranslateMenu();

      if (!googleTranslateElementInitialized) {
        initializeTranslate().catch((error) => {
          isInitializing = false;
          console.error(error);
        });
      }
    });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });

    const handleMenuToggle = () => {
      translateMenuFixedPos = null;
      setTimeout(() => {
        positionTranslateMenu();
      }, 0);
      setTimeout(() => {
        positionTranslateMenu();
      }, 50);
    };
    document.addEventListener('click', handleMenuToggle, true);

    const handleResize = () => {
      translateMenuFixedPos = null;
      positionTranslateMenu();
    };
    window.addEventListener('resize', handleResize, { passive: true });

    const animationFrameId = window.requestAnimationFrame(() => {
      syncGoogleTranslateHostToSlot();
      positionTranslateMenu();
    });

    return () => {
      isMounted = false;
      observer.disconnect();
      window.cancelAnimationFrame(animationFrameId);
      document.removeEventListener('click', handleMenuToggle, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [location.pathname, shouldEnableTranslate]);

  return null;
};

const isApplicantRole = (role?: string | null) => {
  const normalizedRole = role?.toLowerCase();

  return normalizedRole === typesofUsers.applicant.toLowerCase()
    || normalizedRole === typesofUsers.coApplicant.toLowerCase()
    || normalizedRole === typesofUsers.coSigner.toLowerCase();
};

const GoogleTranslate: React.FC = () => {
  const location = useLocation();
  const shouldShowTranslate = shouldEnableGoogleTranslate(location.pathname, getStoredUserRole());

  if (!shouldShowTranslate) {
    return null;
  }

  return <div className="google-translate-slot" data-google-translate-slot="true" />;
};

export default GoogleTranslate;
